import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Ticket, Users } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, fmtErr } from "../../lib/api";

const EMPTY = { title: "", description: "", date: "", time: "19:00", price: 0, capacity: 20, image_url: "" };

export default function EventsTab({ restaurant }) {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [attendees, setAttendees] = useState(null);

  const load = () => api.get(`/restaurants/${restaurant.id}/events`).then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, [restaurant.id]);

  const save = async () => {
    const body = { ...editing, price: Number(editing.price) || 0, capacity: Number(editing.capacity) || 1 };
    try {
      if (editing.id) {
        await api.put(`/restaurants/${restaurant.id}/events/${editing.id}`, body);
      } else {
        await api.post(`/restaurants/${restaurant.id}/events`, body);
      }
      toast.success("Event saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const remove = async (eid) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/events/${eid}`);
      toast.success("Event deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const viewAttendees = async (ev) => {
    try {
      const { data } = await api.get(`/restaurants/${restaurant.id}/events/${ev.id}/attendees`);
      setAttendees({ event: ev, list: data });
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div data-testid="events-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-muted-foreground">
          Prepaid ticketed events appear on your public page. Ticket payments go through your own Stripe (connect it in Settings).
        </p>
        <Button data-testid="add-event-btn" size="sm" className="rounded-full shrink-0"
          onClick={() => setEditing({ ...EMPTY })}>
          <Plus className="w-4 h-4 mr-2" /> New event
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {events.map((ev) => (
          <div key={ev.id} data-testid={`event-card-${ev.id}`} className="rounded-2xl border border-border/60 bg-card p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-outfit font-medium text-lg flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-primary" /> {ev.title}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">{ev.date} · {ev.time}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button data-testid={`attendees-btn-${ev.id}`} size="sm" variant="outline" className="rounded-full h-8"
                  onClick={() => viewAttendees(ev)}>
                  <Users className="w-3.5 h-3.5" />
                </Button>
                <Button data-testid={`edit-event-${ev.id}`} size="sm" variant="outline" className="rounded-full h-8"
                  onClick={() => setEditing({ ...EMPTY, ...ev })}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button data-testid={`delete-event-${ev.id}`} size="sm" variant="ghost" className="rounded-full h-8 text-destructive"
                  onClick={() => remove(ev.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            {ev.description && <p className="text-sm text-muted-foreground mt-2">{ev.description}</p>}
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm">
                <span>{ev.price > 0 ? `£${Number(ev.price).toFixed(2)} / ticket` : "Free entry"}</span>
                <span data-testid={`event-sold-${ev.id}`} className="text-muted-foreground">{ev.sold} / {ev.capacity} sold</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-background overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${Math.min(100, ev.capacity ? (ev.sold / ev.capacity) * 100 : 0)}%` }} />
              </div>
            </div>
          </div>
        ))}
        {events.length === 0 && (
          <p className="text-sm text-muted-foreground md:col-span-2" data-testid="no-events-msg">
            No events yet — create a ticketed event (wine dinners, tasting nights) to sell seats in advance.
          </p>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-card border-border/60 max-h-[85vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-outfit">{editing.id ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2" data-testid="event-editor">
                <Input data-testid="event-title-input" placeholder="Event title (e.g. Summer tasting menu)" value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="bg-background border-border/60" />
                <Textarea data-testid="event-description-input" rows={2} placeholder="Description (optional)" value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="bg-background border-border/60" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Date</Label>
                    <Input data-testid="event-date-input" type="date" value={editing.date}
                      onChange={(e) => setEditing({ ...editing, date: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Time</Label>
                    <Input data-testid="event-time-input" type="time" value={editing.time}
                      onChange={(e) => setEditing({ ...editing, time: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Price per ticket (£)</Label>
                    <Input data-testid="event-price-input" type="number" min="0" step="0.01" value={editing.price}
                      onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Capacity</Label>
                    <Input data-testid="event-capacity-input" type="number" min="1" value={editing.capacity}
                      onChange={(e) => setEditing({ ...editing, capacity: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                  </div>
                </div>
                <Input data-testid="event-image-input" placeholder="Image URL (optional)" value={editing.image_url}
                  onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} className="bg-background border-border/60" />
                <Button data-testid="save-event-btn" onClick={save} disabled={!editing.title || !editing.date} className="w-full rounded-full">
                  Save event
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!attendees} onOpenChange={() => setAttendees(null)}>
        <DialogContent className="bg-card border-border/60 max-h-[85vh] overflow-y-auto">
          {attendees && (
            <>
              <DialogHeader><DialogTitle className="font-outfit">Attendees — {attendees.event.title}</DialogTitle></DialogHeader>
              <div className="mt-2 space-y-3" data-testid="attendees-list">
                {attendees.list.map((a) => (
                  <div key={a.id} data-testid={`attendee-${a.id}`} className="rounded-xl border border-border/40 p-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">{a.name} <span className="text-muted-foreground font-normal">· {a.qty} ticket{a.qty === 1 ? "" : "s"}</span></p>
                      <p className="text-xs text-muted-foreground">{a.email}</p>
                    </div>
                    <span className="text-xs font-mono text-primary">{a.code}</span>
                  </div>
                ))}
                {attendees.list.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="no-attendees-msg">No paid tickets yet.</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
